/**
 * Centralized Logger — Production-Safe Logging
 *
 * Provides gated logging that only outputs in development mode.
 * Prevents console spam in production while maintaining error visibility.
 */

// `import.meta.env` is a Vite-only feature. The Magic Patterns runtime
// does NOT expose it, so a bare `import.meta.env.DEV` throws
// "Cannot read properties of undefined (reading 'DEV')" at module load
// time — and since logger.ts is imported by almost everything, the entire
// app fails to boot. Optional chaining makes this read safe everywhere.
const IS_DEV = import.meta.env?.DEV ?? false;

/**
 * Debug logging - only in development
 */
export function debug(...args: any[]): void {
  if (IS_DEV) {
    console.log('[DEBUG]', ...args);
  }
}

/**
 * Info logging - gated, can be enabled in production if needed
 */
export function info(...args: any[]): void {
  if (IS_DEV) {
    console.log('[INFO]', ...args);
  }
}

/**
 * Warning logging - always allowed
 */
export function warn(...args: any[]): void {
  console.warn('[WARN]', ...args);
}

/**
 * Error logging - always allowed
 */
export function error(...args: any[]): void {
  console.error('[ERROR]', ...args);
}

/**
 * Group logging - only in development
 */
export function group(label: string): void {
  if (IS_DEV) {
    console.group(label);
  }
}

/**
 * End group - only in development
 */
export function groupEnd(): void {
  if (IS_DEV) {
    console.groupEnd();
  }
}

/**
 * Safe JSON parse with fallback
 */
export function safeJsonParse<T>(json: string, fallback: T, context?: string): T {
  try {
    return JSON.parse(json) as T;
  } catch (err) {
    error(`Failed to parse JSON${context ? ` (${context})` : ''}:`, err);
    return fallback;
  }
}

/**
 * Error-reporting seam (F-REL-1, RC audit §9).
 *
 * Called by every error boundary's componentDidCatch. Today it only logs
 * locally via `error()`, but exposes a global hook (`window.__mpReportError`)
 * that future telemetry wiring (Sentry, custom backend) can attach to without
 * touching every boundary again. Keeping the seam tiny and side-effect-free
 * is the whole point — the deliverable is the *call site*, not the transport.
 */
export interface ReportErrorContext {
  boundary: string;
  questionId?: string;
  componentStack?: string;
  extra?: Record<string, unknown>;
}
declare global {
  interface Window {
    __mpReportError?: (err: Error, context: ReportErrorContext) => void;
  }
}
export function reportError(err: Error, context: ReportErrorContext): void {
  // Always log locally so dev/staging keep current behaviour.
  error(`[${context.boundary}] ${err.message}`, {
    stack: err.stack,
    componentStack: context.componentStack,
    questionId: context.questionId,
    ...context.extra
  });
  // Forward to any installed reporter. Wrapped in try/catch so a broken
  // reporter never crashes the app's last-resort error path.
  try {
    if (typeof window !== 'undefined' && typeof window.__mpReportError === 'function') {
      window.__mpReportError(err, context);
    }
  } catch {

    /* swallow — telemetry must never crash the boundary */}
}